import consumer from './consumer';
import { Turbo } from '@hotwired/turbo-rails';

/**
 * Initializes a subscription to background task notifications for a specific user and organization
 * This should be replaced by a turbo_stream_from helper in the view, no need to design a custom channel
 * However, turbo_stream_from helpers are having trouble with asset compilation hence this solution
 * @param {string|number} organizationId - The ID of the organization to subscribe to
 * @param {string|number} userId - The ID of the user to subscribe as
 * @param {string|number} actionClass - The class name of the background task being executed
 * @param {function} finallyCallback - A callback function to be called when the background task is completed
 * @param {boolean} markAsConsumed - Whether to mark the task as consumed after download
 * @param {function} alertCallback - A callback for alert-style websocket updates
 * @returns {Object} An object containing the subscription and methods to manage it
 */
export const initBackgroundTaskNotifications = (
  organizationId,
  userId,
  actionClass,
  finallyCallback = () => {},
  markAsConsumed = false,
  alertCallback = () => {},
) => {
  let subscription = null;
  const createSubscription = () => {
    subscription = consumer.subscriptions.create(
      {
        channel: 'BackgroundTaskNotificationsChannel',
        organization_id: organizationId,
        user_id: userId,
        action_class: actionClass
      },
      {
        /**
         * Handles incoming messages from the WebSocket connection
         * @param {Object} data - The broadcasted data
         * @param {string} data.target - The type of target ('alert' or 'download')
         * @param {string} data.html - The HTML content to be inserted
         * @param {string} data.actionClass - The class name of the background task
         */
        received(data) {
          const target = data.target;

          if (target === 'alert') {
            this.handleAlert(data);
          } else if (target === 'download') {
            this.handleDownload(data);
          } else if (target === 'refresh') {
            this.handleRefresh(data);
          }
        },

        /**
         * Updates the alert target div with new alert content
         * @param {Object} data - The alert data containing HTML content and action class
         */
        handleAlert(data) {
          const taskElement = document.querySelector(
            `.background-task-status[data-action-class="${data.action_class}"]`
          );

          if (taskElement) {
            const alertTarget = taskElement.querySelector('.alert-target');

            if (alertTarget) {
              // eslint-disable-next-line no-unsanitized/property
              alertTarget.innerHTML = data.html;
            }
            if (taskElement.dataset.reloadFrame) {
              fetch(window.location.href, { headers: { Accept: "text/vnd.turbo-stream.html" }})
                .then(r => r.text())
                .then(html => Turbo.renderStreamMessage(html));
            }
          }

          alertCallback(data);
        },

        /**
         * Initiates a file download by creating and clicking a temporary link
         * @param {Object} data - The download data
         * @param {string} data.url - The URL to download the file from
         */
        handleDownload(data) {
          const downloadId = data.background_id;
          const downloadedKey = `downloaded_${downloadId}`;

          // Check if the file has already been downloaded in this session, skip download from another tabs if true
          if (localStorage.getItem(downloadedKey) === 'true') {
            return;
          }

          localStorage.setItem(downloadedKey, 'true');

          // Workaround that doesn't trigger JS events like beforeunload. This is useful to be able to have background
          // exports on pages with Zone::Chart for example (beforeunload destroys the chart).
          fetch(data.url).then(async response => {
            finallyCallback();

            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');

            link.href = objectUrl;
            link.download = data.filename;
            link.click();
            URL.revokeObjectURL(objectUrl);
          }).catch(error => {
            console.error('Background download failed', error);
          }).finally(() => {
            if (markAsConsumed) {
              this.markAsConsumed(downloadId);
            }
            setTimeout(() => {
              localStorage.removeItem(downloadedKey);
            }, 3000);
          });
        },

        markAsConsumed(downloadId) {
          const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

          fetch(`/organizations/${organizationId}/background_tasks/${downloadId}/consume`, {
            method: 'PATCH',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
              'X-CSRF-Token': csrfToken,
            },
          }).catch((error) => console.error('Failed to mark task as consumed: ', error));
        },

        handleRefresh(data) {
          const taskElement = document.querySelector(
            `.background-task-status[data-action-class="${data.action_class}"]`
          );

          if (taskElement.dataset.reloadFrame) {
            Turbo.visit(window.location.href, { action: 'replace', frame: taskElement.dataset.reloadFrame });
          }
        }
      },
    );

    return subscription;
  };
  const forceReconnect = () => {
    if (subscription) {
      subscription.unsubscribe();
    }

    return createSubscription();
  };

  // User comes back from another tab, force reconnect in case its lost
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      forceReconnect();
    }
  });

  subscription = createSubscription();

  return {
    subscription,
    forceReconnect
  };
};
